import { useCurrentBrandHubDomainId } from '@brandhub/hooks/useCurrentBrandHubDomainId';
import { zodResolver } from '@hookform/resolvers/zod';
import { AiLanguageSelect } from '@mailings/components/ai-draft-pitch-modal/components/common/AiLanguageSelect';
import { AnnouncementFormFields } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/AnnouncementFormFields';
import { ResearchFormFields } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/ResearchFormFields';
import { ThoughtLeadershipFormFields } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/ThoughtLeadershipFormFields';
import { PitchTypeSelect } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/PitchTypeSelect';
import {
  isAnnouncementFormValues,
  isResearchFormValues,
  isThoughtLeadershipFormValues,
  MailingObjectivesFormType,
  PitchType,
} from '@mailings/components/creators/personalized-mailing-creator-wizard/types/mailingObjectivesForm.types';
import { getValidationSchemaForPitchType } from '@mailings/components/creators/personalized-mailing-creator-wizard/validations/getValidationSchemaForPitchType.validations';
import { DEFAULT_TONE_OF_VOICE } from '@mailings/constants';
import { Text } from '@semcore/ui/typography';
import { useAiLanguageOptions } from '@src/hooks/useAiLanguageOptions';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormProvider, useForm, Resolver, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface MailingObjectivesFormHandle {
  submit: () => void;
}

interface MailingObjectivesFormProps {
  mailingId?: number;
  formValues: MailingObjectivesFormType | null;
  shouldRegenerate: boolean | (() => boolean);
  prefilledValues?: { storyGoal?: string; storyLink?: string };
  hiddenPitchTypes?: PitchType[];
  syncStoryGoalToExpertReasoning?: boolean;
  onDirtyStateChange: (isDirty: boolean) => void;
  onSubmitObjectives: (formValues: MailingObjectivesFormType) => void;
  onUpdateObjectives: (formValues: MailingObjectivesFormType) => void;
  onNextStep: () => void;
}

export const MailingObjectivesForm = forwardRef<
  MailingObjectivesFormHandle,
  MailingObjectivesFormProps
>(function MailingObjectivesForm(
  {
    mailingId,
    formValues,
    shouldRegenerate,
    prefilledValues,
    hiddenPitchTypes,
    syncStoryGoalToExpertReasoning,
    onDirtyStateChange,
    onSubmitObjectives,
    onUpdateObjectives,
    onNextStep,
  },
  ref,
) {
  const { t } = useTranslation(['mailings']);
  const languageOptions = useAiLanguageOptions();

  const [brandHubDomainId] = useCurrentBrandHubDomainId();

  const initialPitchType = formValues?.pitchType ?? 'personalization_announcement';

  const resolver: Resolver<MailingObjectivesFormType> = (values, context, options) => {
    const pitchType = values.pitchType;
    const schema = getValidationSchemaForPitchType(pitchType);
    return (zodResolver(schema) as Resolver<MailingObjectivesFormType>)(values, context, options);
  };

  const formMethods = useForm<MailingObjectivesFormType>({
    resolver,
    defaultValues: {
      pitchType: initialPitchType,
      campaignIdeationId: formValues?.campaignIdeationId,
      brandHubDomainId: formValues?.brandHubDomainId ?? brandHubDomainId,
      language: formValues?.language ?? languageOptions[0]?.value.toString() ?? '',
      toneOfVoice: formValues?.toneOfVoice ?? DEFAULT_TONE_OF_VOICE,
      ...(initialPitchType === 'personalization_announcement' &&
        formValues &&
        isAnnouncementFormValues(formValues) && {
          storyGoal: formValues.storyGoal,
          storyLink: formValues.storyLink ?? '',
          storyDocumentFile: formValues.storyDocumentFile ?? null,
          isEarlyAccess: formValues.isEarlyAccess ?? false,
          earlyAccessExpireDate: formValues.earlyAccessExpireDate,
          brandHubStoryId: formValues.brandHubStoryId ?? undefined,
          brandHubStoryTitle: formValues.brandHubStoryTitle ?? undefined,
        }),
      ...(initialPitchType === 'personalization_research' &&
        formValues &&
        isResearchFormValues(formValues) && {
          researchFindings: formValues.researchFindings,
          researchDescription: formValues.researchDescription,
          researchSources: formValues.researchSources,
          storyLink: formValues.storyLink ?? '',
          storyDocumentFile: formValues.storyDocumentFile ?? null,
          brandHubResourceId: formValues.brandHubResourceId ?? undefined,
          brandHubResourceName: formValues.brandHubResourceName ?? undefined,
        }),
      ...(initialPitchType === 'personalization_leadership' &&
        formValues &&
        isThoughtLeadershipFormValues(formValues) && {
          expertName: formValues.expertName,
          expertRole: formValues.expertRole,
          expertIntroduction: formValues.expertIntroduction,
          expertReasoning: formValues.expertReasoning,
          expertVoiceId: formValues.expertVoiceId,
        }),
    },
    mode: 'onChange',
  });

  const { control, formState, handleSubmit } = formMethods;
  const { isDirty } = formState;

  useEffect(() => {
    onDirtyStateChange(isDirty);
  }, [isDirty, onDirtyStateChange]);

  const pitchType = useWatch({ control, name: 'pitchType' });

  const handleFormSubmit = (objectivesFormValues: MailingObjectivesFormType) => {
    const shouldRegenerateValue =
      typeof shouldRegenerate === 'function' ? shouldRegenerate() : shouldRegenerate;

    if (!isDirty && !shouldRegenerateValue) {
      onNextStep();
      return;
    }

    if (formValues && mailingId) {
      onUpdateObjectives(objectivesFormValues);
      return;
    }

    onSubmitObjectives(objectivesFormValues);
  };

  useImperativeHandle(ref, () => ({
    submit: () => void handleSubmit(handleFormSubmit)(),
  }));

  useEffect(() => {
    if (!syncStoryGoalToExpertReasoning || pitchType !== 'personalization_leadership') return;

    const values = formMethods.getValues();
    const storyGoal =
      'storyGoal' in values && typeof values.storyGoal === 'string' ? values.storyGoal : undefined;

    if (storyGoal) {
      formMethods.setValue('expertReasoning', storyGoal, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pitchType]);

  useEffect(() => {
    if (prefilledValues?.storyGoal) {
      formMethods.setValue('storyGoal', prefilledValues.storyGoal, { shouldDirty: true });
    }
    if (prefilledValues?.storyLink) {
      formMethods.setValue('storyLink', prefilledValues.storyLink, { shouldDirty: true });
    }
  }, [prefilledValues, formMethods]);

  return (
    <div className="mx-auto flex max-w-[800px] flex-col">
      <FormProvider {...formMethods}>
        <Text
          bold
          className="mb-2"
          size={400}
          tag="h1">
          {t('mailings:personalizedMailingCreatorWizard.steps.objectives.title')}
        </Text>
        <Text
          className="mb-6"
          color="text-secondary"
          tag="p">
          {t('mailings:personalizedMailingCreatorWizard.steps.objectives.description')}
        </Text>
        <form
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(handleFormSubmit)}>
          <PitchTypeSelect excludedTypes={hiddenPitchTypes} />
          {pitchType === 'personalization_announcement' && <AnnouncementFormFields />}
          {pitchType === 'personalization_leadership' && <ThoughtLeadershipFormFields />}
          {pitchType === 'personalization_research' && <ResearchFormFields />}

          <div className="flex w-full flex-col gap-6">
            <AiLanguageSelect className="w-[600px]" />
          </div>
        </form>
      </FormProvider>
    </div>
  );
});
